import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { faqData, faqRPData, faqCategories, faqPartnerCategories } from './mobileFAQ-data';
import { BaseModalDirective } from '@app/components/modals/base-modal';
import { ServiceConfigService } from '@app/services/service-config.service';
import { TranslateService } from '@ngx-translate/core';
declare let gtag: Function;

interface FaqCategory {
  id: string;
  title: string;
  icon: string;
  questionKeyList: string[];
}

interface FaqQuestion {
  key: string;
  value: string;
  show: boolean;
}

@Component({
    selector: 'mobileFAQ',
    templateUrl: './mobileFAQ.component.html',
    styleUrls: ['./mobileFAQ.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class mobileFAQComponent extends BaseModalDirective implements OnInit {
  private readonly medicalAppealQuestionKeys = ['FAQ_KEY_45', 'FAQ_KEY_46'];
  data = [];
  userType = '';
  mobileUser = sessionStorage.getItem('isMobileUser') === 'true' ? true : false;
  awsTranslateSwitch = false;
  
  faqRevampSwitch = false;
  medicalAppealsSwitch = false;
  categories: FaqCategory[] = [];
  selectedCategory: FaqCategory | null = null;
  filteredQuestions: FaqQuestion[] = [];
  showCategoryView = true;
  searchQuery = '';
  searchResults: FaqQuestion[] = [];
  isSearching = false;
  language;
  isSearchInteraction: boolean = false;

  constructor(
    protected modalRef: BsModalRef,
    protected builder: FormBuilder,
    private serverConfig: ServiceConfigService,
    public translateService: TranslateService
  ) {
      super(modalRef, builder);
   }

  ngOnInit(): void {
    this.awsTranslateSwitch = this.serverConfig.getServerConfig().awsTranslateSwitch;
    this.faqRevampSwitch = this.serverConfig.getServerConfig()?.faqRevampSwitch;
    this.medicalAppealsSwitch = this.serverConfig.getServerConfig()?.medicalAppealsSwitch;
    this.language = sessionStorage.getItem('currentLang');
    if(sessionStorage.getItem('userType')) {
      this.userType = sessionStorage.getItem('userType');
    }
    this.setData();    
  }

  onLoad(response) {
    if(response.usertype) {
      this.userType = response.usertype;
      this.setData();
    }
  }

  setData() {
    if (this.userType && this.userType === 'RP') {
      this.data = [...faqRPData];
      this.categories = [...faqPartnerCategories];
    } else {
      this.data = JSON.parse(JSON.stringify(faqData));
      this.categories = [...faqCategories];
      // Conditionally set FAQ_KEY_25 value based on awsTranslateSwitch
      const faqItem = this.data[0].questions.find(q => q.key === 'FAQ_KEY_25');
      if (faqItem) {
        faqItem.value = this.awsTranslateSwitch ? 'FAQ_VALUE_25' : 'FAQ_VALUE_25_1';
      }

      if (!this.medicalAppealsSwitch) {
        this.data.forEach(section => {
          section.questions = section.questions.filter(q => !this.medicalAppealQuestionKeys.includes(q.key));
        });
      }
    }
  }

  showHide(num) {
    num.show = !num.show
  }

  cardClickHandler(event: any, item: any) {
    const target = event.target as HTMLElement;
    if(target.tagName.toLowerCase() !== 'a') {
      item.show = !item.show;
    }
  }

  onSearchInput(event: any): void {
    const searchTxt = (event.target.value || '').toLowerCase().trim();
    this.searchQuery = event.target.value;
    
    if (!searchTxt) {
      this.isSearching = false;
      this.searchResults = [];
      return;
    }

    if (!this.isSearchInteraction) {
      gtag('event', 'faq_search_event', {
        'event_category': 'faq_search',
        'event_label': 'input_start',
      });
      this.isSearchInteraction = true;
    }
    
    this.isSearching = true;
    
    let allQuestions: FaqQuestion[] = [];
    allQuestions = this.data[0]?.questions || [];
    
    this.searchResults = allQuestions.filter(q => {
      const questionText = this.translateService.instant(q.key).toLowerCase();
      const answerText = this.translateService.instant(q.value).toLowerCase();
      return questionText.includes(searchTxt) || answerText.includes(searchTxt);
    }).map(q => ({
      ...q,
      show: true
    }));
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.isSearching = false;
    this.searchResults = [];
  }

  onCategoryClick(categoryId: string): void {
    const category = this.categories.find(c => c.id === categoryId);
    if (!category) return;

    this.selectedCategory = category;
    this.showCategoryView = false;

    const allQuestions = this.data[0]?.questions || [];
    this.filteredQuestions = allQuestions.filter(q => 
      category.questionKeyList?.includes(q.key)
    ).sort((a, b) =>
      category.questionKeyList?.indexOf(a.key) - category.questionKeyList?.indexOf(b.key)
    ).map(q => ({
      ...q,
      show: true
    }));

    this.scrollToTop();
  }

  goBackToCategories(): void {
    this.selectedCategory = null;
    this.filteredQuestions = [];
    this.showCategoryView = true;
    this.scrollToTop();
  }

  isAppealCategorySelected(): boolean {
    return this.selectedCategory?.id === 'appeal-faqs';
  }

  isEligibilityAppealsHeader(questionKey: string): boolean {
    return questionKey === 'FAQ_KEY_27';
  }

  isMedicalAppealsHeader(questionKey: string): boolean {
    return questionKey === 'FAQ_KEY_45';
  }

  scrollToTop(): void {
    setTimeout(() => {
      
      const modal = document.querySelector('.modal');
      if (modal) {
        modal.scrollTop = 0;
      }
    }, 50);
  }

  formatFaqAnswer(translatedValue: string): string {
    if (!translatedValue || !this.isArabicOrPersianLang()) {
      return translatedValue;
    }
    return translatedValue.replace(/888-345-5575/g, '5575-345-888');
  }

  isArabicOrPersianLang(): boolean {
    const selectedLang = sessionStorage.getItem('globalTranslatedLan') || sessionStorage.getItem('currentLang') || 'en';
    return selectedLang === 'ar' || selectedLang === 'fa';
  }

}